// ============================================================================
// CONFIGURACIÓN PAGE - Configuración de correo
// Pantalla /configuracion. Gestiona SMTP + remitente + plantilla del correo
// por Empresa (general) y por Sede, consumiendo el módulo email-config.
//   · ADMIN: edita la Empresa y cualquier sede de su empresa (selector).
//   · GESTOR_SEDE: edita su sede; puede ver la de Empresa en solo lectura.
// Estilo y patrones alineados con las páginas admin existentes (sin inventar).
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Mail,
  Server,
  Send,
  Save,
  Loader2,
  Info,
  Lock,
  Building2,
  MapPin,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sitesApi, type SiteSelect } from '../api/sites';
import {
  emailConfigApi,
  type EmailConfig,
  type SmtpSecurity,
  type EmailConfigScope,
  type UpdateEmailConfigPayload,
  type TestEmailPayload,
} from '../api/emailConfig';
import { toast } from '../components/ui/Toast';

const COMPANY_KEY = 'company';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface ConfigFormData {
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: SmtpSecurity;
  smtpUser: string;
  smtpPassword: string;
  fromName: string;
  fromEmail: string;
  subjectTemplate: string;
  headerText: string;
  footerText: string;
  isActive: boolean;
}

export function ConfiguracionPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Selector de ámbito (Empresa o una sede): clave = COMPANY_KEY | siteId
  const [siteOptions, setSiteOptions] = useState<SiteSelect[]>([]);
  const [targetKey, setTargetKey] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [current, setCurrent] = useState<EmailConfig | null>(null);
  const [testTo, setTestTo] = useState<string>(user?.email || '');

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ConfigFormData>({
    defaultValues: {
      smtpHost: '',
      smtpPort: 587,
      smtpSecurity: 'STARTTLS',
      smtpUser: '',
      smtpPassword: '',
      fromName: '',
      fromEmail: '',
      subjectTemplate: '',
      headerText: '',
      footerText: '',
      isActive: true,
    },
  });

  // Ámbito derivado de la selección
  const scope: EmailConfigScope = targetKey === COMPANY_KEY ? 'COMPANY' : 'SITE';
  const siteId = targetKey === COMPANY_KEY ? undefined : targetKey;
  // GESTOR_SEDE solo puede LEER la config de Empresa (no editarla)
  const readOnly = !isAdmin && scope === 'COMPANY';

  const selectedSiteName =
    targetKey === COMPANY_KEY
      ? null
      : siteOptions.find((s) => s.id === targetKey)?.name ||
        user?.activeSite?.name ||
        null;

  // ── Inicializar selección + cargar sedes (solo ADMIN) ─────────────────────
  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      setTargetKey(COMPANY_KEY);
      sitesApi
        .getForSelect()
        .then(setSiteOptions)
        .catch(() => toast.error('Error', 'No se pudieron cargar las sedes'));
    } else {
      // GESTOR_SEDE: fijado a su sede activa
      setTargetKey(user.activeSiteId || '');
    }
  }, [user, isAdmin]);

  // ── Cargar configuración del ámbito seleccionado ──────────────────────────
  const load = useCallback(async () => {
    if (!targetKey) return;
    setIsLoading(true);
    try {
      const cfg = await emailConfigApi.get(scope, siteId);
      setCurrent(cfg);
      reset({
        smtpHost: cfg.smtpHost,
        smtpPort: cfg.smtpPort,
        smtpSecurity: cfg.smtpSecurity,
        smtpUser: cfg.smtpUser,
        smtpPassword: '',
        fromName: cfg.fromName,
        fromEmail: cfg.fromEmail,
        subjectTemplate: cfg.subjectTemplate,
        headerText: cfg.headerText,
        footerText: cfg.footerText,
        isActive: cfg.isActive,
      });
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo cargar la configuración');
    } finally {
      setIsLoading(false);
    }
  }, [targetKey, scope, siteId, reset]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Guardar ───────────────────────────────────────────────────────────────
  const onSave = async (data: ConfigFormData) => {
    if (readOnly) return;
    setIsSaving(true);
    try {
      const payload: UpdateEmailConfigPayload = {
        scope,
        siteId,
        smtpHost: data.smtpHost.trim(),
        smtpPort: Number(data.smtpPort),
        smtpSecurity: data.smtpSecurity,
        smtpUser: data.smtpUser?.trim() || undefined,
        fromName: data.fromName.trim(),
        fromEmail: data.fromEmail.trim(),
        subjectTemplate: data.subjectTemplate?.trim() || undefined,
        headerText: data.headerText?.trim() || undefined,
        footerText: data.footerText?.trim() || undefined,
        isActive: data.isActive,
      };
      // Solo se envía la contraseña si el usuario teclea una nueva
      if (data.smtpPassword) payload.smtpPassword = data.smtpPassword;

      await emailConfigApi.update(payload);
      toast.success('Guardado', 'Configuración de correo actualizada');
      await load(); // refresca hasPassword / exists
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Probar envío (sin guardar) ────────────────────────────────────────────
  const onTest = async () => {
    const valid = await trigger([
      'smtpHost',
      'smtpPort',
      'smtpSecurity',
      'fromName',
      'fromEmail',
    ]);
    if (!valid) {
      toast.error('Faltan datos', 'Revisa los campos obligatorios antes de probar');
      return;
    }
    if (!EMAIL_RE.test(testTo.trim())) {
      toast.error('Destino no válido', 'Indica un email de destino válido');
      return;
    }

    const data = getValues();
    setIsTesting(true);
    try {
      const payload: TestEmailPayload = {
        scope,
        siteId,
        smtpHost: data.smtpHost.trim(),
        smtpPort: Number(data.smtpPort),
        smtpSecurity: data.smtpSecurity,
        smtpUser: data.smtpUser?.trim() || undefined,
        fromName: data.fromName.trim(),
        fromEmail: data.fromEmail.trim(),
        to: testTo.trim(),
      };
      if (data.smtpPassword) payload.smtpPassword = data.smtpPassword;

      const res = await emailConfigApi.test(payload);
      if (res.ok) toast.success('Prueba enviada', res.message);
      else toast.error('Falló la prueba', res.message);
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo realizar la prueba');
    } finally {
      setIsTesting(false);
    }
  };

  const inputCls = (hasError?: boolean) =>
    `w-full px-4 py-2.5 border rounded-xl text-sm ${
      hasError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
    } disabled:bg-gray-50 disabled:text-gray-500`;

  // GESTOR_SEDE sin sede activa: no puede configurar nada
  if (!isAdmin && !user?.activeSiteId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración de correo</h1>
          <p className="text-sm text-gray-500 mt-1">SMTP y remitente del correo de presupuestos</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          No tienes una sede activa, por lo que no hay configuración de correo que gestionar.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración de correo</h1>
          <p className="text-sm text-gray-500 mt-1">
            SMTP, remitente y plantilla del correo con el que se envían los presupuestos
          </p>
        </div>
      </div>

      {/* Nota de resolución */}
      <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-xl p-4 text-sm flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>
          Al enviar un presupuesto se usa el SMTP de la <strong>sede</strong>; si no tiene
          configuración o falla, se usa el de la <strong>Empresa</strong>.
        </span>
      </div>

      {/* Selector de ámbito */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Ámbito de configuración</label>
        {isAdmin ? (
          <select
            value={targetKey}
            onChange={(e) => setTargetKey(e.target.value)}
            className="w-full md:w-96 px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            <option value={COMPANY_KEY}>Empresa (general)</option>
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={targetKey}
            onChange={(e) => setTargetKey(e.target.value)}
            className="w-full md:w-96 px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            {user?.activeSiteId && (
              <option value={user.activeSiteId}>
                {user.activeSite?.name ? `${user.activeSite.name} (mi sede)` : 'Mi sede'}
              </option>
            )}
            <option value={COMPANY_KEY}>Empresa (general) — solo lectura</option>
          </select>
        )}
        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
          {scope === 'COMPANY' ? (
            <>
              <Building2 className="h-3.5 w-3.5" /> Configuración general de la empresa
            </>
          ) : (
            <>
              <MapPin className="h-3.5 w-3.5" /> Configuración de la sede{selectedSiteName ? `: ${selectedSiteName}` : ''}
            </>
          )}
        </p>
      </div>

      {/* Avisos contextuales */}
      {readOnly && (
        <div className="bg-gray-50 border border-gray-200 text-gray-600 rounded-xl p-4 text-sm flex items-start gap-2">
          <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
          Vista de solo lectura: la configuración de la Empresa solo puede editarla un administrador.
        </div>
      )}
      {scope === 'SITE' && current && !current.exists && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          Esta sede aún no tiene configuración propia: <strong>hereda la de la Empresa</strong>. Rellena
          los campos y guarda para asignarle una configuración propia.
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 px-1">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando configuración…
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSave)} className="space-y-6">
          {/* Bloque SMTP */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Server className="h-5 w-5 text-blue-600" /> Servidor SMTP
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Servidor (host) *</label>
                <input
                  type="text"
                  disabled={readOnly}
                  {...register('smtpHost', { required: 'El servidor es obligatorio' })}
                  className={inputCls(!!errors.smtpHost)}
                  placeholder="smtp.miproveedor.com"
                />
                {errors.smtpHost && (
                  <p className="mt-1 text-xs text-red-500">{errors.smtpHost.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puerto *</label>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  disabled={readOnly}
                  {...register('smtpPort', {
                    required: 'El puerto es obligatorio',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Puerto entre 1 y 65535' },
                    max: { value: 65535, message: 'Puerto entre 1 y 65535' },
                  })}
                  className={inputCls(!!errors.smtpPort)}
                  placeholder="587"
                />
                {errors.smtpPort && (
                  <p className="mt-1 text-xs text-red-500">{errors.smtpPort.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seguridad *</label>
                <select
                  disabled={readOnly}
                  {...register('smtpSecurity', { required: true })}
                  className={inputCls(!!errors.smtpSecurity)}
                >
                  <option value="STARTTLS">STARTTLS (recomendado, puerto 587)</option>
                  <option value="SSL">SSL/TLS (puerto 465)</option>
                  <option value="NONE">Ninguna (sin cifrado)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                <input
                  type="text"
                  disabled={readOnly}
                  {...register('smtpUser')}
                  className={inputCls(false)}
                  placeholder="usuario@miproveedor.com"
                  autoComplete="off"
                />
              </div>
            </div>

            {!readOnly && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                <input
                  type="password"
                  {...register('smtpPassword')}
                  className={inputCls(false)}
                  placeholder={current?.hasPassword ? '•••••••• (sin cambios)' : 'Contraseña SMTP'}
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {current?.hasPassword
                    ? 'Hay una contraseña guardada. Déjalo vacío para conservarla.'
                    : 'La contraseña se almacena cifrada y nunca se muestra.'}
                </p>
              </div>
            )}
          </div>

          {/* Bloque Remitente */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Mail className="h-5 w-5 text-blue-600" /> Remitente
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del remitente *</label>
                <input
                  type="text"
                  disabled={readOnly}
                  {...register('fromName', { required: 'El nombre del remitente es obligatorio' })}
                  className={inputCls(!!errors.fromName)}
                  placeholder="Nombre de la clínica"
                />
                {errors.fromName && (
                  <p className="mt-1 text-xs text-red-500">{errors.fromName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email del remitente *</label>
                <input
                  type="email"
                  disabled={readOnly}
                  {...register('fromEmail', {
                    required: 'El email del remitente es obligatorio',
                    pattern: { value: EMAIL_RE, message: 'Email no válido' },
                  })}
                  className={inputCls(!!errors.fromEmail)}
                  placeholder="presupuestos@miclinica.com"
                />
                {errors.fromEmail && (
                  <p className="mt-1 text-xs text-red-500">{errors.fromEmail.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Bloque Plantilla */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <ShieldCheck className="h-5 w-5 text-blue-600" /> Plantilla del correo
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asunto
                <span className="ml-1 text-xs text-gray-400 font-normal">(si se deja vacío se usa uno por defecto)</span>
              </label>
              <input
                type="text"
                disabled={readOnly}
                {...register('subjectTemplate')}
                className={inputCls(false)}
                placeholder="Su presupuesto"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto de cabecera</label>
                <textarea
                  disabled={readOnly}
                  {...register('headerText')}
                  rows={3}
                  className={`${inputCls(false)} resize-none`}
                  placeholder="Texto opcional bajo el saludo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto de pie</label>
                <textarea
                  disabled={readOnly}
                  {...register('footerText')}
                  rows={3}
                  className={`${inputCls(false)} resize-none`}
                  placeholder="Texto opcional en el pie del correo"
                />
              </div>
            </div>
            {!readOnly && (
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pt-1">
                <input type="checkbox" {...register('isActive')} className="h-4 w-4 rounded border-gray-300" />
                Configuración activa
              </label>
            )}
          </div>

          {/* Probar envío (no aplica en solo lectura) */}
          {!readOnly && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
              <div className="flex items-center gap-2 text-gray-900 font-semibold">
                <Send className="h-5 w-5 text-blue-600" /> Probar envío
              </div>
              <p className="text-sm text-gray-500">
                Envía un correo de prueba con los valores de arriba (sin guardar) para comprobar que el SMTP funciona.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enviar prueba a</label>
                  <input
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="destino@ejemplo.com"
                  />
                </div>
                <button
                  type="button"
                  onClick={onTest}
                  disabled={isTesting}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-700 hover:bg-gray-800 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Probar envío
                </button>
              </div>
            </div>
          )}

          {/* Guardar */}
          {!readOnly && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md shadow-blue-100 disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar configuración
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

export default ConfiguracionPage;
